import * as React from 'react';
import { AspectRatio } from 'react-aspect-ratio';
import ReactPlayer from 'react-player';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

import LazyAvatar from '../../../components/Avatar/LazyAvatar/LazyAvatar';
import { Item } from '../../../components/Photoswipe/Photoswipe';
import Icons from '../../../themes/icons';
import EmptyContent, { ContentType } from './EmptyContent';
import PanelSkeleton from './PanelSkeleton';

export type MediaItem = {
  id: string;
  label: string;
  placeholderSrc: string;
  src: string;
  video?: string;
  displayOrder: number | null;
  postOnInstagramFeed?: boolean;
  postOnInstagramStories?: boolean;
  postOnInstagramStories2x?: boolean;
  postOnInstagramStories3x?: boolean;
  postOnInstagramReels?: boolean;
  postOnTikTokVideo?: boolean;
};

type MediaContainerProps = {
  items: MediaItem[];
  isLoading: boolean;
  contentType: ContentType;
};

const VideoSlide: React.FC<{ url: string; poster: string }> = ({ url, poster }) => {
  const [playing, setPlaying] = React.useState(true);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const visibleRef = React.useRef(true);
  const videoElRef = React.useRef<HTMLVideoElement | null>(null);

  const findVideoEl = React.useCallback(() => {
    if (videoElRef.current && document.contains(videoElRef.current)) {
      return videoElRef.current;
    }
    const el = wrapperRef.current?.querySelector('video') ?? null;
    videoElRef.current = el;
    return el;
  }, []);

  const tryAutoplay = React.useCallback(() => {
    const video = findVideoEl();
    if (!video) return;
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {
        video.muted = true;
        video.play().catch(() => {
          setPlaying(false);
        });
      });
    }
  }, [findVideoEl]);

  React.useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        visibleRef.current = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        if (!visibleRef.current) {
          setPlaying(false);
        }
      },
      { threshold: [0, 0.5, 1] },
    );
    io.observe(wrapper);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || !visibleRef.current) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      e.preventDefault();
      setPlaying((p) => !p);
    };

    document.addEventListener('keydown', onKeyDown);

    const raf = window.requestAnimationFrame(tryAutoplay);

    return () => {
      io.disconnect();
      document.removeEventListener('keydown', onKeyDown);
      window.cancelAnimationFrame(raf);
    };
  }, [tryAutoplay]);

  return (
    <Box
      ref={wrapperRef}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ReactPlayer
        width='100%'
        height='100%'
        playing={playing}
        url={url}
        controls
        playsinline
        muted
        onReady={tryAutoplay}
        config={{ file: { attributes: { poster, autoPlay: true, playsInline: true } } }}
      />
    </Box>
  );
};

const renderPlatformIcon = (x: MediaItem) => {
  if (
    x.postOnInstagramStories ||
    x.postOnInstagramStories2x ||
    x.postOnInstagramStories3x
  ) {
    return <Icons.Content1StoryIcon />;
  }
  if (x.postOnInstagramFeed) {
    return <Icons.ContentFeedIcon />;
  }
  if (x.postOnInstagramReels) {
    return <Icons.ContentReelIcon />;
  }
  if (x.postOnTikTokVideo) {
    return (
      <Box
        display='flex'
        alignItems='center'
        justifyContent='center'
        sx={{
          width: '34px',
          height: '34px',
          background: 'rgba(255, 255, 255, 0.90)',
          borderRadius: '50%',
        }}
      >
        <Icons.TiktokIcon width={16} height={20} />
      </Box>
    );
  }
  return null;
};

const MediaContainer: React.FC<MediaContainerProps> = ({ isLoading, items, contentType }) => {
  if (isLoading) {
    return <PanelSkeleton />;
  }
  return items.length > 0 ? (
    <Grid container spacing={1}>
      {items.map((x) => {
        const platformIcon = renderPlatformIcon(x);
        return (
          <Grid item xs={5} sm={4} md={2.5} lg={2.2} xl={2}>
            <Box sx={{ position: 'relative' }}>
              {platformIcon && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  {platformIcon}
                </Box>
              )}
              <AspectRatio key={x.id} ratio='3/4' style={{ borderRadius: 12, overflow: 'hidden' }}>
                <Item
                  itemProps={{
                    original: x.src,
                    content: x.video ? (
                      <VideoSlide url={x.video} poster={x.src} />
                    ) : undefined,
                  }}
                >
                  <LazyAvatar
                    variant='square'
                    alt={x.label}
                    src={x.src}
                    placeholderSrc={x.placeholderSrc}
                    isVideo={Boolean(x.video)}
                    sx={{ borderRadius: '12px', width: '100%', height: '100%' }}
                  />
                </Item>
              </AspectRatio>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  ) : (
    <EmptyContent contentType={contentType} />
  );
};

export default MediaContainer;
